import { noteServerNow } from "../state/serverTime.js";
import { queryClient, queryKey, invalidateServerQueries } from "./query.js";
import { requestScope, assertRequestScope, onRequestReadReset } from "./scope.js";
import { isCancelledError } from "@tanstack/react-query";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly detail: string,
    public readonly details: unknown = null,
  ) {
    super(`${code}: ${detail}`);
    this.name = "ApiError";
  }
}

interface Envelope<T> {
  ok: true;
  serverNow: number;
  data: T;
}

const conditional = new Map<string, { etag: string; envelope: Envelope<unknown> }>();
onRequestReadReset(() => conditional.clear());

/** JSON request against the same-origin API (Vite proxies to the server in dev). Unwraps the envelope. */
async function request<T>(method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE", path: string, body: unknown, generation: number, signal?: AbortSignal): Promise<T> {
  assertRequestScope(generation);
  signal?.throwIfAborted();
  const cached = method === "GET" ? conditional.get(path) : undefined;
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: { accept: "application/json", ...(body !== undefined ? { "content-type": "application/json" } : {}), ...(cached ? { "if-none-match": cached.etag } : {}) },
    ...(signal ? { signal } : {}),
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(path, init);
  assertRequestScope(generation);
  signal?.throwIfAborted();
  if (res.status === 304 && cached) {
    const serverNow = Number(res.headers.get("x-zend-server-now"));
    if (Number.isFinite(serverNow) && serverNow > 0) noteServerNow(serverNow);
    return cached.envelope.data as T;
  }
  const text = await res.text();
  assertRequestScope(generation);
  signal?.throwIfAborted();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok || !json || typeof json !== "object" || !("ok" in json)) {
    const p = (json ?? {}) as { code?: string; detail?: string; details?: unknown };
    throw new ApiError(res.status, p.code ?? `HTTP_${res.status}`, p.detail ?? res.statusText, p.details ?? null);
  }
  const env = json as Envelope<T>;
  const etag = res.headers.get("etag");
  if (method === "GET" && etag) conditional.set(path, { etag, envelope: env });
  noteServerNow(env.serverNow);
  return env.data;
}

/** One account-scoped Query client deduplicates concurrent reads and cancels their fetches on logout. */
/** `fresh`: the read goes to the network regardless of a deduplicated read in flight or cached data, then refreshes the cache. */
export async function api<T>(method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE", path: string, body?: unknown, options?: { fresh?: boolean }): Promise<T> {
  const { generation } = requestScope();
  if (method === "GET" && options?.fresh) {
    const data = await request<T>(method, path, body, generation);
    assertRequestScope(generation);
    queryClient.setQueryData(queryKey(path), data);
    return data;
  }
  if (method === "GET") {
    for (let attempt = 0; ; attempt++) {
      try {
        const data = await queryClient.fetchQuery({ queryKey: queryKey(path), queryFn: ({ signal }) => request<T>(method, path, body, generation, signal) });
        assertRequestScope(generation);
        return data;
      } catch (error) {
        assertRequestScope(generation);
        if (!isCancelledError(error) || attempt >= 4) throw error;
      }
    }
  }
  invalidateServerQueries();
  try { return await request<T>(method, path, body, generation); }
  finally { if (generation === requestScope().generation) invalidateServerQueries(); }
}
